import { AuthContextType } from '@/components/types';
import { TAROBASE_CONFIG } from '@/lib/config';
import { debounce } from '@/utils/debounce';
import { useAuth } from '@pooflabs/web';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Copy, LogOut, RefreshCw, Wallet } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type WalletButtonVariant = 'light' | 'dark';

interface WalletButtonProps {
  variant?: WalletButtonVariant;
}

// Seekscape brand palette — cosmic dark with electric violet
const SEEKSCAPE = {
  violet: '#9b6ef3',         // primary — hsl(262 83% 66%)
  violetDim: '#7c4fd4',      // deeper violet
  violetGlow: 'rgba(155, 110, 243, 0.35)',
  violetBorder: 'rgba(155, 110, 243, 0.5)',
  violetBorderHover: 'rgba(155, 110, 243, 0.85)',
  bgDeep: '#07060f',         // near-black with blue tint
  bgCard: '#0f0d1e',         // card surface
  bgItem: 'rgba(155, 110, 243, 0.06)',
  bgItemHover: 'rgba(155, 110, 243, 0.12)',
  textPrimary: '#f0eeff',
  textMuted: '#7a74a8',
  textStrong: '#ffffff',
  divider: 'rgba(155, 110, 243, 0.15)',
  green: '#22c55e',
};

// Isolated button styles that reset all inherited/global styles
const getIsolatedButtonBase = (): React.CSSProperties => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  fontFamily: "'Syne', system-ui, sans-serif",
});

const getVariantStyles = (_variant: WalletButtonVariant) => {
  // Both variants use the Seekscape cosmic theme — outlined style matching settings button
  return {
    button: {
      background: 'rgba(139, 92, 246, 0.12)',
      color: '#a78bfa',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      boxShadow: '0 0 6px rgba(139, 92, 246, 0.2)',
    },
    buttonHover: {
      background: 'rgba(139, 92, 246, 0.25)',
      borderColor: 'rgba(139, 92, 246, 0.6)',
      boxShadow: '0 0 12px rgba(139, 92, 246, 0.45)',
    },
    popup: {
      backgroundColor: SEEKSCAPE.bgCard,
      border: `1px solid ${SEEKSCAPE.violetBorder}`,
    },
    text: SEEKSCAPE.textPrimary,
    textMuted: SEEKSCAPE.textMuted,
    textStrong: SEEKSCAPE.textStrong,
    cardBg: `linear-gradient(135deg, rgba(155, 110, 243, 0.18) 0%, rgba(124, 79, 212, 0.12) 50%, rgba(7, 6, 15, 0.95) 100%)`,
    itemBg: SEEKSCAPE.bgItem,
    itemBorder: 'rgba(155, 110, 243, 0.2)',
    itemHoverBg: SEEKSCAPE.bgItemHover,
    divider: SEEKSCAPE.divider,
    iconColor: SEEKSCAPE.violet,
  };
};

export const WalletButton: React.FC<WalletButtonProps> = ({ variant = 'light' }) => {
  const styles = getVariantStyles(variant);
  const { user, loading, login, logout } = useAuth() as AuthContextType;
  const [isOpen, setIsOpen] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
  const lastFetchTime = useRef<number>(0);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const FETCH_COOLDOWN = 5000;

  const fetchBalance = async () => {
    if (!user?.address || !TAROBASE_CONFIG.rpcUrl) {
      setBalance(null);
      setBalanceLoading(false);
      return;
    }

    setBalanceLoading(true);

    try {
      // Fetch native SOL balance
      const nativeSolResponse = await fetch(TAROBASE_CONFIG.rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'getBalance',
          params: [user.address],
        }),
      });

      const nativeSolData = await nativeSolResponse.json();
      if (nativeSolData.error)
        throw new Error(nativeSolData.error.message || 'Failed to fetch balance');

      const balanceInSol = nativeSolData.result.value / 1_000_000_000;
      setBalance(balanceInSol);
    } catch (err) {
      console.error('Error fetching SOL balance:', err);
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const refetch = () => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime.current;

    if (timeSinceLastFetch < FETCH_COOLDOWN) {
      return;
    }

    lastFetchTime.current = now;
    fetchBalance();
  };

  useEffect(() => {
    lastFetchTime.current = 0;
    fetchBalance();
  }, [user?.address]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePopupPosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      const isMobile = window.innerWidth < 480;
      const margin = 8;

      // right = distance from the right edge of the viewport to the right edge of the button
      const nextRight = isMobile ? margin : Math.max(window.innerWidth - rect.right, margin);
      const nextTop = Math.max(rect.bottom + 8, margin);

      setPopupPosition({
        top: nextTop,
        right: nextRight,
      });
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // Debounce scroll/resize updates to at most once per frame (16 ms)
    const throttledUpdate = debounce(updatePopupPosition);

    updatePopupPosition();
    window.addEventListener('resize', throttledUpdate);
    window.addEventListener('scroll', throttledUpdate, { passive: true, capture: true });
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', throttledUpdate);
      window.removeEventListener('scroll', throttledUpdate, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogin = () => {
    // Close any open overlay state synchronously before triggering the wallet
    // connect flow. On Android (Mobile Wallet Adapter) the OS fires an intent://
    // redirect that opens Phantom, but the Android system "Allow" dialog appears
    // on top of the web view while the web page's fixed overlays are still
    // mounted. Those overlays intercept touch events and make the system dialog
    // button un-tappable. Closing overlays first and deferring login() to the
    // next tick gives React time to commit the unmount before the intent fires.
    setIsOpen(false);
    window.dispatchEvent(new Event('poof:close-overlays'));
    setTimeout(async () => {
      try {
        await login();
      } catch (error) {
        console.error('Failed to login', error);
      }
    }, 0);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to logout', error);
    }
  };

  const getNetworkInfo = () => {
    const chain = TAROBASE_CONFIG.chain;
    const isInMockMode = chain === 'offchain';
    const isMainnet = chain === 'solana_mainnet';
    const isSurfnet = chain === 'surfnet';
    return {
      name: isInMockMode ? 'Poofnet' : isMainnet ? 'Mainnet' : isSurfnet ? 'Surfnet' : 'Devnet',
      dotColor: isInMockMode
        ? '#3b82f6' // blue for poofnet
        : isMainnet
          ? '#22c55e' // green for mainnet
          : isSurfnet
            ? '#3b82f6' // blue for surfnet
            : '#f97316', // orange for devnet
    };
  };

  const formatBalance = (bal: number | null) => {
    if (bal === null) return '0.00';
    return bal.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const shortenAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyAddress = async () => {
    if (user?.address) {
      await navigator.clipboard.writeText(user.address);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 2000);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      {!user ? (
        <motion.button
          onClick={loading ? undefined : handleLogin}
          style={{
            ...getIsolatedButtonBase(),
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
            ...styles.button,
          }}
          whileHover={loading ? undefined : styles.buttonHover}
          whileTap={loading ? undefined : { scale: 0.97 }}
        >
          <Wallet style={{ height: '15px', width: '15px', opacity: 0.9 }} />
        </motion.button>
      ) : (
        <div style={{ position: 'relative' }}>
          <motion.button
            ref={buttonRef}
            onClick={() => setIsOpen(!isOpen)}
            style={{
              ...getIsolatedButtonBase(),
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
              ...styles.button,
            }}
            whileHover={styles.buttonHover}
            whileTap={{ scale: 0.97 }}
          >
            <Wallet style={{ height: '15px', width: '15px', opacity: 0.9 }} />
          </motion.button>

          {isMounted &&
            createPortal(
              <AnimatePresence>
                {isOpen && (
                  <React.Fragment>
                    {/* Backdrop with blur overlay */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2147483645,
                        pointerEvents: 'auto',
                        backgroundColor: 'rgba(7, 6, 15, 0.45)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                      }}
                      onMouseDown={() => setIsOpen(false)}
                    />
                    <motion.div
                      ref={popupRef}
                      initial={{ opacity: 0, scale: 0.96, y: -6 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96, y: -6 }}
                      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                      style={{
                        position: 'fixed',
                        top: `${popupPosition.top}px`,
                        right: `${popupPosition.right}px`,
                        width: window.innerWidth < 480 ? `calc(100vw - 16px)` : '260px',
                        maxWidth: '260px',
                        backgroundColor: styles.popup.backgroundColor,
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        borderRadius: '14px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 24px rgba(155, 110, 243, 0.12), 0 2px 6px rgba(0, 0, 0, 0.4)',
                        border: styles.popup.border,
                        overflow: 'hidden',
                        zIndex: 2147483647,
                        pointerEvents: 'auto',
                      }}
                      onMouseDown={event => event.stopPropagation()}
                      onClick={event => event.stopPropagation()}
                    >
                {/* Network Indicator */}
                <div
                  style={{
                    padding: '9px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      backgroundColor: getNetworkInfo().dotColor,
                      boxShadow: `0 0 6px ${getNetworkInfo().dotColor}40`,
                    }}
                  />
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: styles.textMuted,
                    }}
                  >
                    {getNetworkInfo().name}
                  </span>
                </div>

                {/* Balance Card */}
                <div
                  style={{
                    margin: '0 12px 12px',
                    padding: '12px',
                    background: styles.cardBg,
                    borderRadius: '10px',
                    position: 'relative',
                  }}
                >
                  {/* Decorative circle */}
                  <div
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Wallet style={{ height: '18px', width: '18px', color: styles.iconColor }} />
                  </div>

                  <div style={{ marginLeft: '54px' }}>
                    <div
                      style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: styles.text,
                        marginBottom: '2px',
                      }}
                    >
                      {getNetworkInfo().name} Wallet
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        style={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: styles.textStrong,
                        }}
                      >
                        {formatBalance(balance)} SOL
                      </span>
                      <motion.button
                        onClick={refetch}
                        disabled={balanceLoading}
                        style={{
                          ...getIsolatedButtonBase(),
                          padding: '3px',
                          backgroundColor: 'rgba(155, 110, 243, 0.1)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: balanceLoading ? 'not-allowed' : 'pointer',
                          justifyContent: 'center',
                          opacity: balanceLoading ? 0.5 : 1,
                        }}
                        whileHover={{
                          backgroundColor: 'rgba(155, 110, 243, 0.22)',
                        }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <RefreshCw
                          style={{
                            height: '12px',
                            width: '12px',
                            color: styles.textMuted,
                            animation: balanceLoading ? 'spin 1s linear infinite' : 'none',
                          }}
                        />
                      </motion.button>
                    </div>
                  </div>
                </div>

                {/* Wallet Address */}
                <div style={{ padding: '0 12px', marginBottom: '10px' }}>
                  <motion.button
                    onClick={copyAddress}
                    style={{
                      ...getIsolatedButtonBase(),
                      width: '100%',
                      padding: '9px 12px',
                      backgroundColor: styles.itemBg,
                      border: `1px solid ${styles.itemBorder}`,
                      borderRadius: '8px',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s ease',
                    }}
                    whileHover={{
                      backgroundColor: styles.itemHoverBg,
                      borderColor:
                        variant === 'dark' ? 'rgba(107, 114, 128, 1)' : 'rgba(209, 213, 219, 1)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        color: styles.text,
                      }}
                    >
                      {shortenAddress(user.address)}
                    </span>
                    {justCopied ? (
                      <Check style={{ height: '14px', width: '14px', color: '#10b981' }} />
                    ) : (
                      <Copy style={{ height: '14px', width: '14px', color: styles.textMuted }} />
                    )}
                  </motion.button>
                </div>

                {/* Divider */}
                <div
                  style={{
                    height: '1px',
                    backgroundColor: styles.divider,
                    margin: '0 12px',
                  }}
                />

                {/* Logout Button */}
                <div style={{ padding: '8px 12px 12px' }}>
                  <motion.button
                    onClick={handleLogout}
                    style={{
                      ...getIsolatedButtonBase(),
                      width: '100%',
                      padding: '9px 12px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      gap: '10px',
                      transition: 'all 0.2s ease',
                    }}
                    whileHover={{ backgroundColor: styles.itemBg }}
                  >
                    <LogOut style={{ height: '15px', width: '15px', color: styles.text }} />
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: styles.text,
                      }}
                    >
                      Log out
                    </span>
                  </motion.button>
                </div>
                    </motion.div>
                  </React.Fragment>
                )}
              </AnimatePresence>,
              document.body
            )}
        </div>
      )}

      {/* Keyframes for animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default WalletButton;
